import { useEffect, useRef, useState } from 'react'
import type {
  AgendaItem, BoardMember, Commitment, Meeting, NextMeeting,
} from '../api/client'
import { api, apiUrl, priorityRank, type Priority } from '../api/client'
import { MeetingChip } from '../components/MeetingChip'
import { AskWidget, BoardStrip } from './Dashboard'
import { AgendaThread } from './Thread'
import { Attachments } from './Attachments'
import { daysUntil, formatDay, meetingDay } from '../dates'
import { EmptyArt } from '../components/EmptyArt'
import { TopicIcon } from '../components/TopicIcon'
import { topicFor } from '../topics'
import { summarise } from '../summarise'

// ─── Home (§4) ──────────────────────────────────────────────────────────────

export function Home({
  next, inbox, commitments, board, onNavigate, onAnswer, onAsked, onCaptured,
}: {
  next: NextMeeting | null
  inbox: AgendaItem[]
  commitments: Commitment[]
  board: BoardMember[]
  onNavigate: (s: 'table' | 'commitments' | 'meetings' | 'board') => void
  onAnswer: (id: string, answer: string) => Promise<void>
  onAsked: (consultId: string) => void
  onCaptured: (text: string) => Promise<void>
}) {
  const unanswered = inbox.filter(i => i.clarifierQuestion && !i.clarifierAnswer)
  const overdue = commitments.filter(c => c.dueDate && new Date(c.dueDate) < new Date())

  return (
    <>
      {/* The five faces first, then the box. Everything below is bookkeeping. */}
      <BoardStrip members={board} onOpen={() => onNavigate('board')} />

      <AskWidget onAsked={onAsked} onCaptured={onCaptured} />

      <MeetingChip next={next} onOpen={() => onNavigate('meetings')} />

      {/* §4: the clarifier arrives as a Home row. Same thread the table renders, so a
          question answered from either place looks like the same conversation. */}
      {unanswered.map(item => (
        <article key={item.id} className="item">
          <AgendaThread item={item} board={board} onAnswer={onAnswer} />
        </article>
      ))}

      {overdue.length > 0 && (
        <div className="card">
          <div className="card__head">
            <span className="card__title">You said you would</span>
            <button className="btn btn--quiet" onClick={() => onNavigate('commitments')}>All</button>
          </div>
          {overdue.slice(0, 3).map(c => (
            <div key={c.id} style={{ marginTop: 'var(--space-2)' }}>
              <div>{c.text}</div>
              <div className="dim">Due {formatDate(c.dueDate!)} · {c.successMetric}</div>
            </div>
          ))}
        </div>
      )}

      {unanswered.length === 0 && overdue.length === 0 && inbox.length === 0 && (
        <p className="dim" style={{ textAlign: 'center', marginTop: 'var(--space-5)' }}>
          Nothing else needs you right now.
        </p>
      )}
    </>
  )
}

// ─── The Table (§6) ────────────────────────────────────────────────
//
// Called the inbox in the spec and in the schema, where an item's status is still
// 'inbox'. The screen is named for what the items actually are: things on the table
// for the next meeting. The lifecycle column keeps its name, because renaming a status
// nobody sees would be a migration for a label.

export function TheTable({
  items, board, onPatch, onAnswer, focusId,
}: {
  items: AgendaItem[]
  board: BoardMember[]
  onPatch: (id: string, action: 'defer' | 'delete') => Promise<void>
  onAnswer: (id: string, answer: string) => Promise<void>
  /** Opened and scrolled to on arrival, so "Answer it" lands on the question itself. */
  focusId?: string | null
}) {
  // §6: grouped by cluster where clustering has run.
  const clusters = new Map<string, AgendaItem[]>()
  for (const item of items) {
    const key = item.clusterLabel ?? ''
    clusters.set(key, [...(clusters.get(key) ?? []), item])
  }

  return (
    <>
      <h1 className="screen-title">The Table</h1>
      <p className="screen-sub">
        {items.length === 0
          ? 'Nothing waiting.'
          : `${items.length} heading for the next meeting${deferred(items)}.`}
      </p>

      {items.length === 0 && (
        <div className="empty">
          <div className="empty__title">Quiet week</div>
          <p>
            That is not a problem. If nothing lands by Friday the board sets its own
            agenda from your numbers and what you have already committed to.
          </p>
        </div>
      )}

      <details className="archive">
        <summary>Everything you have put on the table</summary>
        <Attachments />
      </details>

      {[...clusters.entries()].map(([label, group]) => (
        <section key={label || 'ungrouped'}>
          {label && (
            <h2 className="section-title">{label}</h2>
          )}
          {group.map(item => (
            <TableItem
              key={item.id}
              item={item}
              board={board}
              onPatch={onPatch}
              onAnswer={onAnswer}
              focusId={focusId}
            />
          ))}
        </section>
      ))}
    </>
  )
}


/*
  One item on the table, closed by default.

  Four conversations open at once buried the page: each carries a question, a board
  member's reply, an answer, and its attachments, and stacked full height they push the
  fourth item a screen and a half down. So a closed item is a headline, a date, and enough
  of the exchange to recognise it, and the rest opens on a click.

  The headline is the owner's own first line rather than a generated summary. It is already
  the shortest true description of the item, and generating one would be a model call per
  item per page load to say something slightly worse.
*/
function TableItem({
  item, board, onPatch, onAnswer, focusId,
}: {
  item: AgendaItem
  board: BoardMember[]
  onPatch: (id: string, action: 'defer' | 'delete') => Promise<void>
  onAnswer: (id: string, answer: string) => Promise<void>
  focusId?: string | null
}) {
  const [open, setOpen] = useState(item.id === focusId)
  const card = useRef<HTMLElement>(null)

  /*
    Arriving from "Answer it" on the meeting card. The item is opened and brought into
    view rather than left closed somewhere down a list of five, which is the difference
    between a link that answers the question and one that only changes screens.
  */
  useEffect(() => {
    if (focusId !== item.id) return
    setOpen(true)
    card.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [focusId, item.id])

  const reply = item.consultAnswer ?? item.clarifierQuestion

  /* The board is asking and has had no answer. A system item asks in its own text and
     carries no clarifier, so it counts too: see the note in Thread.tsx. */
  const waiting = !item.clarifierAnswer
    && (Boolean(item.clarifierQuestion) || item.source === 'system')

  const label = summarise(item.rawText)
  const gist = headline(item.rawText)

  const hasChips = item.source === 'system' || item.source === 'escalation'
    || Boolean(item.prepPayload) || waiting || item.deferredCount > 0

  return (
    <article ref={card} className={`item item--card${item.source === 'system' ? ' item--system' : ''}`}>
      <button className="item__head" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        {/* What it is about, before it is read. See src/topics.ts. */}
        <TopicIcon name={topicFor(item.rawText)} size={36} />

        {/*
          A six-word label leads, with the owner's own sentence under it.

          Leading with the sentence meant a table of six rows was six truncated sentences,
          several of them opening with the same words, and nothing to scan. The label is cut
          from the item's own words in their own order, so it can be unhelpful but never
          wrong; see src/summarise.ts. The sentence is not replaced, only demoted.
        */}
        <span className="item__headline">
          <span className="item__title">{label || headline(item.rawText)}</span>
          <span className="item__when">{shortDate(item.consultAskedAt ?? item.submittedAt)}</span>
          <span className="item__toggle">{open ? 'Hide' : 'Open'} ›</span>
        </span>

        {/* Suppressed when the label already is the sentence, which happens on short
            items: "Raise?" does not want saying twice. */}
        {label && gist !== label && <span className="item__gist">{gist}</span>}

        {!open && reply && (
          <span className="item__preview">{preview(reply)}</span>
        )}

        {/*
          Only when there is something to say. The toggle used to live here, which meant
          every row drew this line whether or not it had a chip on it, and a row with
          nothing to report carried a band of white with "Open" alone at the far right of
          it. The toggle is on the title line now and this row has earned its own absence.
        */}
        {hasChips && (
          <span className="item__meta">
            {item.source === 'system' && <span className="chip chip--moved">From the board</span>}
            {item.source === 'escalation' && <span className="chip chip--conditional">From a consult</span>}
            {item.prepPayload && <span className="chip chip--support">Prepped</span>}
            {waiting && <span className="chip chip--conditional">Needs your answer</span>}
            {item.deferredCount > 0 && (
              <span className="chip chip--oppose">Deferred {item.deferredCount}×</span>
            )}
          </span>
        )}
      </button>

      {open && (
        <div className="item__body">
          <AgendaThread item={item} board={board} onAnswer={onAnswer} />
          <Attachments agendaItemId={item.id} />

          <div className="item__actions">
            <button className="btn btn--quiet" onClick={() => onPatch(item.id, 'defer')}>
              Not this week
            </button>
            <button className="btn btn--danger" onClick={() => onPatch(item.id, 'delete')}>
              Remove
            </button>
          </div>
        </div>
      )}
    </article>
  )
}

/* The owner's first sentence, or a trimmed first line. Never mid-word. */
function headline(text: string): string {
  const clean = text.replace(/\s+/g, ' ').trim()
  const stop = clean.search(/[.?!](\s|$)/)
  const first = stop > 20 ? clean.slice(0, stop + 1) : clean
  return first.length <= 72 ? first : `${first.slice(0, 71).replace(/\s\S*$/, '')}\u2026`
}

function preview(text: string): string {
  const clean = text.replace(/\s+/g, ' ').trim()
  return clean.length <= 140 ? clean : `${clean.slice(0, 139).replace(/\s\S*$/, '')}\u2026`
}

function shortDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const sameYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleDateString(undefined,
    sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' })
}

/*
  §6 header: always show the deferred count. It is a gentle accountability signal and it is
  what makes the roll-forward mechanic legible rather than items quietly reappearing.
*/
function deferred(items: AgendaItem[]): string {
  const count = items.filter(i => i.deferredCount > 0).length
  return count === 0 ? '' : `, ${count} rolled over`
}

// ─── Meetings (§9) ──────────────────────────────────────────────────────────

export function Meetings({
  meetings, next, onOpen, onAnswerWaiting,
}: {
  meetings: Meeting[]
  next: NextMeeting | null
  onOpen: (meetingId: string, live: boolean) => void
  onAnswerWaiting: () => void
}) {
  return (
    <>
      <h1 className="screen-title">Meetings</h1>
      <p className="screen-sub">What is scheduled, and what the board has already decided.</p>

      <MeetingChip next={next} onAnswerWaiting={onAnswerWaiting} />

      {meetings.length === 0 && (
        <div className="empty">
          <EmptyArt name="sessions" />
          <div className="empty__title">No meetings yet</div>
          <p>Your first one runs on the schedule set in your board configuration.</p>
        </div>
      )}

      {/*
        A minute book, not a list of cards.

        This screen used to render one card per meeting whose entire content was the word
        "Scheduled" and a chip reading "scheduled", because the endpoint behind it filtered
        to meetings that had not happened yet. It could not have shown a history if it had
        wanted to. Now every meeting arrives with the first line of what it decided, and the
        screen is what the marketing page has been promising: a dated record you can read
        down.
      */}
      {[...group(meetings).entries()].map(([month, rows]) => (
        <section key={month}>
          <h2 className="section-title">{month}</h2>
          <ol className="ledger">
            {rows.map(m => <LedgerRow key={m.id} m={m} onOpen={onOpen} />)}
          </ol>
        </section>
      ))}
    </>
  )
}

/** Meetings by the month they happened in, newest first, order preserved. */
function group(meetings: Meeting[]): Map<string, Meeting[]> {
  const out = new Map<string, Meeting[]>()

  for (const m of meetings) {
    const when = m.completedAt ?? m.scheduledFor
    const label = when
      ? new Date(when).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
      : 'Undated'
    out.set(label, [...(out.get(label) ?? []), m])
  }

  return out
}

function LedgerRow({ m, onOpen }: { m: Meeting; onOpen: (id: string, live: boolean) => void }) {
  const when = m.completedAt ?? m.scheduledFor
  const held = m.status === 'complete'

  // A meeting in session opens too, into the live view rather than the minutes. It is the
  // one row on this screen worth interrupting what you were doing for.
  const running = m.status === 'running'
  const openable = held || running

  const Row = openable ? 'button' : 'div'

  /*
    "4-1", or nothing.

    Short on purpose: the minutes page spells the vote out in a sentence, and a ledger wants
    the number. Conditional support counts as support here, because a seat that said yes with
    a condition still carried it; the condition is in the minutes, one tap away.
  */
  const forVotes = m.supportVotes + m.conditionalVotes
  const vote = held && (forVotes > 0 || m.opposeVotes > 0)
    ? `${forVotes}-${m.opposeVotes}`
    : null
  const dissent = m.opposeVotes > 0

  return (
    <li className={`led${openable ? '' : ' led--ahead'}`}>
      <Row
        className={`led__row${openable ? ' led__row--open' : ''}`}
        {...(openable ? { onClick: () => onOpen(m.id, running), type: 'button' as const } : {})}
      >
      <span className="led__day">
        {when
          ? new Date(when).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
          : '—'}
      </span>

      <span className="led__body">
        {/* The decision is the row. Everything else on the line is context for it. */}
        <span className="led__what">
          {running
            ? 'Sitting now.'
            : m.headline ?? (held ? 'Held. No decision was recorded.' : 'Not held yet.')}
        </span>
        {running && (
          <span className="led__note">The board is meeting. Open it to watch.</span>
        )}
        {m.isFallback && !running && (
          <span className="led__note">Nothing was on the table, so the board set its own agenda.</span>
        )}
      </span>

      <span className="led__right">
        {/*
          How the vote went, which is the one fact that tells you whether a meeting is worth
          opening. The row carried the first sentence of the recommendation and nothing about
          whether the board agreed on it, so a unanimous week and a knife-edge one read
          identically down the page.
        */}
        {vote && <span className={`led__vote${dissent ? ' led__vote--split' : ''}`}>{vote}</span>}

        <span className="led__count">
          {running
            ? 'In session'
            : m.itemCount > 0
              ? `${m.itemCount} ${m.itemCount === 1 ? 'item' : 'items'}`
              : ''}
        </span>
      </span>

      {/* Only on rows that go somewhere. A chevron on a meeting that has not happened is a
          promise the row cannot keep. */}
      {openable && (
        <svg className="led__go" viewBox="0 0 24 24" width="16" height="16" fill="none"
             stroke="currentColor" strokeWidth="2" aria-hidden focusable="false">
          <path d="m9 18 6-6-6-6" />
        </svg>
      )}
      </Row>
    </li>
  )
}

// ─── Commitments (§10) ──────────────────────────────────────────────────────

type SortKey = 'priority' | 'newest' | 'oldest'

/*
  Priority first, because it is the only one of the three that answers the question the
  screen is for. Newest and oldest answer "what did I just agree to" and "what have I been
  sitting on", which are both worth asking and neither is the default.
*/
const SORTS: { key: SortKey; label: string }[] = [
  { key: 'priority', label: 'Priority' },
  { key: 'newest',   label: 'Newest' },
  { key: 'oldest',   label: 'Oldest' },
]

const PRIORITY_LABEL: Record<Priority, string> = {
  high: 'High priority',
  normal: 'Normal priority',
  low: 'Low priority',
}


export function Commitments({
  commitments, highlight, onHighlighted, onResolve, onAnswered,
}: {
  commitments: Commitment[]
  /** One the owner has just been sent here to look at, or null. */
  highlight?: string | null
  onHighlighted?: () => void
  onResolve: (id: string, status: string, note?: string) => Promise<void>
  onAnswered: () => Promise<void>
}) {

  /*
    Three sections (UI SPEC §10), not two. "Coming up" put a commitment due Tuesday next
    to one with no date at all, which reads as the same urgency and is not.

    Undated sorts last inside Open. A commitment should never arrive without a date: the
    extractor clamps the horizon into 1-180 days, so one that has none came from a path
    that bypassed it. The nightly sweep logs that; here it just sinks to the bottom rather
    than sitting above things that are actually happening.
  */
  // Calendar days, not instants: see src/dates.ts. Parsed as UTC these all read a day
  // early, which put a commitment due today into "past due".
  const days = (c: Commitment) => (c.dueDate ? daysUntil(c.dueDate) : Infinity)

  /*
    By priority, the three sections are the spine and the sort reorders inside each.
    Lateness is a fact about the world and it outranks a ranking the owner picked: a
    high-priority action due in ninety days is still not more urgent than one that was due
    last Tuesday.

    By date, the sections go and the list is one run.

    That is a reversal, and it is the right one. Sorting within sections meant "newest"
    put a commitment first inside its own band and sixth on the screen, under five past-due
    cards, so the control did not do what its label said. Asking for newest first is the
    owner overriding the default spine on purpose, and answering with a different order
    than the one named is worse than any ordering.

    Lateness does not get hidden by this. Every past-due card keeps its red chip wherever
    it lands, which is what actually carries the warning; the section heading was only ever
    grouping it.
  */
  const [sort, setSort] = useState<SortKey>('priority')
  const byDate = sort !== 'priority'

  /*
    Let go of the highlight once it has been seen. Without this, every later visit to this
    screen scrolls to a commitment that stopped being new several visits ago.
  */
  useEffect(() => {
    if (!highlight || !onHighlighted) return
    const id = setTimeout(onHighlighted, 8000)
    return () => clearTimeout(id)
  }, [highlight, onHighlighted])

  const ordered = (items: Commitment[]) => [...items].sort((a, b) =>
    sort === 'priority'
      // Then by date inside a band, or the three high ones come back in whatever order the
      // server happened to return and the list reshuffles itself on every reload.
      ? priorityRank(a.priority) - priorityRank(b.priority) || days(a) - days(b)
      : sort === 'newest'
        ? b.createdAt.localeCompare(a.createdAt)
        : a.createdAt.localeCompare(b.createdAt))

  const pastDue = ordered(commitments.filter(c => days(c) < 0))
  const dueSoon = ordered(commitments.filter(c => days(c) >= 0 && days(c) <= 14))
  const open    = ordered(commitments.filter(c => days(c) > 14))

  return (
    <>
      <h1 className="screen-title">Actions</h1>
      <p className="screen-sub">The board follows up. That is most of the point.</p>

      {commitments.length > 1 && (
        <div className="sortbar">
          <span className="sortbar__label">Sort</span>
          {SORTS.map(o => (
            <button
              key={o.key}
              className={`sortbar__opt${sort === o.key ? ' is-on' : ''}`}
              aria-pressed={sort === o.key}
              onClick={() => setSort(o.key)}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}

      {commitments.length === 0 && (
        <div className="empty">
          <EmptyArt name="followup" />
          <div className="empty__title">Nothing outstanding</div>
          <p>Commitments appear here after a meeting, with a date and a way to check them.</p>
        </div>
      )}

      {byDate ? (
        <Section items={ordered(commitments)} lateBy={days} highlight={highlight}
                 onResolve={onResolve} onAnswered={onAnswered} />
      ) : (
        <>
          <Section title="Past due" items={pastDue} overdue highlight={highlight}
                   onResolve={onResolve} onAnswered={onAnswered} />
          <Section title="Due soon" items={dueSoon} highlight={highlight}
                   onResolve={onResolve} onAnswered={onAnswered} />
          <Section title="Open" items={open} highlight={highlight}
                   onResolve={onResolve} onAnswered={onAnswered} />
        </>
      )}
    </>
  )
}

function Section({
  title, items, overdue, lateBy, highlight, onResolve, onAnswered,
}: {
  /** Absent when the list is one run, which is what a date sort renders. */
  title?: string
  items: Commitment[]
  /** Every card in this section is late. Set by the "Past due" section. */
  overdue?: boolean
  /**
   * Works it out per card instead. A date-sorted run mixes late and not, and the red chip
   * is the only thing carrying that once the headings are gone.
   */
  lateBy?: (c: Commitment) => number
  highlight?: string | null
  onResolve: (id: string, status: string, note?: string) => Promise<void>
  onAnswered: () => Promise<void>
}) {
  if (items.length === 0) return null

  return (
    <>
      {/* A real heading, not a card label borrowed with an inline margin. "Past due" is a
          division of the screen, and it now looks like one. */}
      {title && <h2 className="section-title">{title}</h2>}
      <div className="itemgrid">
        {items.map(c => (
          <CommitmentRow key={c.id} c={c}
                         overdue={overdue ?? (lateBy ? lateBy(c) < 0 : false)}
                         highlight={highlight === c.id}
                         onResolve={onResolve} onAnswered={onAnswered} />
        ))}
      </div>
    </>
  )
}

/*
  How much this one matters, and the owner's way to disagree.

  A dot rather than a word, because it sits on the date line of every card and three cards
  reading "Normal priority" in a column is noise about the ones that are not. Only high and
  low are drawn at all: normal is the absence of a claim, and giving it a mark of its own
  would make the ordinary case the loud one.

  It cycles rather than opening a menu. Three values on a control the owner will touch twice
  a month does not earn a popover, and the aria-label carries the current state so the tap
  target is not a mystery to anybody who cannot see the colour.
*/
function PriorityDot({ c, onChanged }: { c: Commitment; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false)

  const next: Record<Priority, Priority> = { normal: 'high', high: 'low', low: 'normal' }

  const cycle = async () => {
    if (busy) return
    setBusy(true)
    try {
      await api.post(`/api/commitments/${c.id}/priority`, { priority: next[c.priority] })
      await onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      className={`prio prio--${c.priority}`}
      onClick={cycle}
      disabled={busy}
      title={`${PRIORITY_LABEL[c.priority]}. Tap to change.`}
      aria-label={`${PRIORITY_LABEL[c.priority]}. Change it.`}
    >
      <span className="prio__dot" aria-hidden />
      {c.priority !== 'normal' && <span className="prio__word">{c.priority}</span>}
    </button>
  )
}

function CommitmentRow({
  c, overdue, highlight, onResolve, onAnswered,
}: {
  c: Commitment
  overdue?: boolean
  /** The owner was sent here to look at this one. */
  highlight?: boolean
  onResolve: (id: string, status: string, note?: string) => Promise<void>
  onAnswered: () => Promise<void>
}) {
  const [note, setNote] = useState('')
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const card = useRef<HTMLElement>(null)

  /*
    Bring it into view, once.

    An action accepted from a consult sorts by its due date like any other, so on a busy
    list it arrives below everything already late. Scrolling to it is the difference between
    a toast that claims something happened and a screen that shows it.
  */
  useEffect(() => {
    if (!highlight) return
    card.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [highlight])

  /*
    Long enough to be worth folding. Measured in characters rather than by asking the
    browser how many lines it laid out, because a commitment is a paragraph of instructions
    and the ones worth clamping are obvious from their length: the shortest real one on file
    is nine words, the longest is sixty-one.
  */
  const long = c.text.length > 150

  return (
    <article ref={card} className={`item${highlight ? ' item--new' : ''}`}>
      {highlight && <span className="item__flag">Just added</span>}
      {/*
        The date leads, not the paragraph. A commitment arrives as a full instruction,
        sometimes six lines of it, and the card used to set the whole thing at one size and
        weight with the date underneath in grey. Four of those filled a screen and none of
        them could be skimmed. What somebody scanning this screen wants is when it is due
        and whether it is late.
      */}
      <div className="item__when">
        <PriorityDot c={c} onChanged={onAnswered} />
        {c.dueDate && <span className="item__due">Due {formatDate(c.dueDate)}</span>}
        {overdue && <span className="chip chip--oppose">Past due</span>}
      </div>

      <div className={`item__text${long && !expanded ? ' item__text--clamped' : ''}`}>
        {c.text}
      </div>

      {long && (
        <button className="linkish item__more" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Less' : 'Read all of it'}
        </button>
      )}

      <div className="dim item__metric">{c.successMetric}</div>

      {/*
        §10: "Nobody else in this man's life asks him whether he did the thing he said he'd
        do. That is the product." Saying so before Friday is what makes it a commitment
        rather than an ambush, and naming the member is what makes it a board rather than a
        cron job.
      */}
      {c.followUpBy && c.followUpOn && (
        <div className="promise">
          {c.followUpFace && <img className="promise__face" src={c.followUpFace} alt="" loading="lazy" />}
          <span>{firstName(c.followUpBy)}'s putting this on the table {meetingDay(c.followUpOn)}.</span>
        </div>
      )}

      <Answer c={c} onAnswered={onAnswered} onResolve={onResolve} />

      {!open && (
        <div className="item__actions">
          <button className="btn" onClick={() => setOpen(true)}>Close this out</button>
        </div>
      )}

      {open && (
        <div className="item__ask">
          <input
            className="input"
            placeholder="What actually happened?"
            value={note}
            onChange={e => setNote(e.target.value)}
          />
          <div className="item__actions">
            <button className="btn btn--primary" onClick={() => onResolve(c.id, 'done', note)}>Did it</button>
            <button className="btn" onClick={() => onResolve(c.id, 'slipped', note)}>Slipped</button>
            <button className="btn btn--quiet" onClick={() => onResolve(c.id, 'abandoned', note)}>Dropped it</button>
          </div>
        </div>
      )}
    </article>
  )
}

// ─── Board (§11) ────────────────────────────────────────────────────────────

// ─── Shared helpers ─────────────────────────────────────────────────────────

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]!.toUpperCase()).join('')
}

function formatDate(iso: string): string {
  return formatDay(iso)
}

/** Polls until a predicate holds or the budget runs out. Used for async consults (§5). */
export function usePoll<T>(fetcher: () => Promise<T>, done: (v: T) => boolean, enabled: boolean) {
  const [value, setValue] = useState<T | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    let attempts = 0

    const tick = async () => {
      if (cancelled || attempts++ > 60) return
      try {
        const v = await fetcher()
        if (cancelled) return
        setValue(v)
        if (done(v)) return
      } catch {
        /* keep polling; a transient failure is not a terminal state */
      }
      setTimeout(tick, 2000)
    }

    void tick()
    return () => { cancelled = true }
  }, [enabled])

  return value
}

function firstName(name: string): string {
  return name.split(/\s+/)[0] ?? name
}

/*
  Answering the commitment on the card that asked for it.

  The board asked for something specific and put a date on it, and until now the only thing
  the owner could do here was mark it closed. So the answer had nowhere to go: the figure
  got found, the board never saw it, and the next meeting opened by asking for it again.

  A sentence or a file, because that is how these actually get answered. The file goes
  through the same extraction as anything in the File Cabinet, so a seat reads the overtime
  numbers rather than the words "overtime.pdf", and both land in the board's prefix
  underneath the commitment they answer.
*/
function Answer({
  c, onAnswered, onResolve,
}: {
  c: Commitment
  onAnswered: () => Promise<void>
  onResolve: (id: string, status: string, note?: string) => Promise<void>
}) {
  const [note, setNote] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const picker = useRef<HTMLInputElement>(null)

  /*
    What was just submitted, held only so the next tap can become the outcome note.

    Null means no answer has been sent on this card in this sitting, which is not the same
    as the card having no answers: an owner returning to a card they answered last week is
    not asked again about an answer they have already decided not to close on.
  */
  const [justSent, setJustSent] = useState<string | null>(null)

  const send = async () => {
    if (busy || (!note.trim() && !file)) return
    setBusy(true)
    setError(null)
    try {
      const body = new FormData()
      if (note.trim()) body.append('note', note.trim())
      if (file) body.append('file', file)

      const response = await fetch(apiUrl(`/api/commitments/${c.id}/evidence`), {
        method: 'POST', body, credentials: 'include',
      })
      if (!response.ok) {
        const detail = await response.json().catch(() => null)
        throw new Error(detail?.error ?? 'That did not send')
      }
      /*
        Remembered before the fields are cleared. This is what the card offers back as the
        outcome if the owner says it closes the action, so "Yes" records what they
        actually reported rather than an empty note or a manufactured one.
      */
      setJustSent(note.trim() || file?.name || 'Answered')

      setNote(''); setFile(null)
      await onAnswered()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not send')
    } finally {
      setBusy(false)
    }
  }

  /*
    The question the owner should not have to go looking for.

    Answering and closing were two separate acts, and only the second took the card off the
    list, so somebody who typed "truck-constrained" into the action that asked whether
    delayed jobs were truck- or tech-constrained had answered it in full and still found it
    sitting under Past due. The same complaint as a table item outliving the thing that
    fixed it: doing the work should not leave a second gesture to go and find.

    It is asked rather than assumed because resolving writes outcome memory (SPEC §5.1),
    which is what makes the board's advice in month six better than in month one, and
    because not every submission is a completion. Attaching a progress document and adding a
    second note are both normal, which is why the field says "Add anything else" once there
    is an answer. One tap either way, in place, and Not yet is not a nag: it stands the
    question down for this sitting.
  */
  const closeOut = async () => {
    const outcome = justSent
    setJustSent(null)
    await onResolve(c.id, 'done', outcome ?? undefined)
  }

  return (
    <div className="answer">
      {c.answers.length > 0 && (
        <div className="answer__log">
          {c.answers.map(a => (
            <div className="answer__row" key={a.id}>
              <span className="answer__tick" aria-hidden>&#10003;</span>
              <span>
                {a.note}
                {a.fileName && (
                  <span className="answer__file">
                    {a.note ? ' ' : ''}{a.fileName}
                    {/* Said plainly: an attached scan the board cannot read is not an answer,
                        and the owner should find that out here rather than on Friday. */}
                    {!a.readable && <span className="dim"> · not readable, so type the figures</span>}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {error && <div className="signin__error">{error}</div>}

      {justSent && (
        <div className="answer__close">
          <span className="answer__closeq">Does that close it out?</span>
          {/* Yes and Not yet, because the card asked a yes-or-no question. "Did it" is the
              answer to "what happened?", which is the other button on this card and a
              different question; put against "Does that close it out?" it reads as a
              statement rather than a reply. */}
          <button className="btn btn--small btn--primary" type="button"
                  disabled={busy} onClick={() => void closeOut()}>
            Yes
          </button>
          <button className="btn btn--small" type="button"
                  onClick={() => setJustSent(null)}>
            Not yet
          </button>
        </div>
      )}

      <div className="answer__form">
        <input
          className="input"
          placeholder={c.answers.length ? 'Add anything else' : 'Answer this, or attach the document'}
          value={note}
          onChange={e => setNote(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void send() } }}
        />
        <input
          ref={picker}
          type="file"
          hidden
          accept=".pdf,.csv,.txt,.xlsx,.xlsm,.xls,.png,.jpg,.jpeg,.gif,.webp"
          onChange={e => { setFile(e.target.files?.[0] ?? null); e.target.value = '' }}
        />
        <button className="btn" type="button" onClick={() => picker.current?.click()}>
          {file ? 'Change file' : 'Attach'}
        </button>
        {/* Navy outline, not the gold fill. This button renders on every card whether or
            not there is anything to submit, so on a screen with six commitments the gold
            fill appeared six times, mostly disabled. Gold marks the primary action of a
            screen; a form inside a card is not that. */}
        <button className="btn btn--ghost" type="button"
                disabled={busy || (!note.trim() && !file)} onClick={() => void send()}>
          {busy ? 'Submitting' : 'Submit'}
        </button>
      </div>

      {file && (
        <div className="dim answer__chosen">
          {file.name} · {Math.round(file.size / 1024)} KB
        </div>
      )}
    </div>
  )
}
